export const NOTIFICATION_STATUSES = ['new', 'read'] as const;

export type NotificationStatus = (typeof NOTIFICATION_STATUSES)[number];

export const NOTIFICATION_ACTION_NAMES = ['accept', 'decline', 'reject', 'fill', 'ok'] as const;

export type NotificationActionName = (typeof NOTIFICATION_ACTION_NAMES)[number];

export const NOTIFICATION_ACTION_TYPES = ['default', 'destructive', 'cancel'] as const;

export type NotificationActionType = (typeof NOTIFICATION_ACTION_TYPES)[number];

/**
 * `invitation` answers a workspace invitation: the communications service
 * writes those rows with a `workplace_accept`/`workplace_decline` destiny and
 * a URL that must be called with PUT.
 */
export const NOTIFICATION_ACTION_DESTINIES = [
  'link',
  'request',
  'invitation',
  'no_action_needed',
] as const;

export type NotificationActionDestiny = (typeof NOTIFICATION_ACTION_DESTINIES)[number];

export const NOTIFICATION_MESSAGES = [
  'workspace_invitation_title',
  'workspace_invitation_message',
  'removed_from_workspace_title',
  'removed_from_workspace_message',
  'notification_invoice_created_title',
  'notification_invoice_created_message',
  'fixed_payments_cycle_expiring_title',
  'fixed_payments_cycle_expiring_message',
  'employee_sent_draft_cycle_title',
  'employee_sent_draft_cycle_message',
  'employer_approved_draft_cycle_title',
  'employer_approved_draft_cycle_message',
  'employer_rejected_draft_cycle_title',
  'employer_rejected_draft_cycle_message',
  'employer_created_cycle_title',
  'employer_created_cycle_message',
  'employer_shared_cycle_framework_title',
  'employer_shared_cycle_framework_message',
  'employee_requested_cycle_cancel_title',
  'employee_requested_cycle_cancel_message',
  'employer_canceled_cycle_title',
  'employer_canceled_cycle_message',
  'employer_dismissed_cycle_cancel_request_title',
  'employer_dismissed_cycle_cancel_request_message',
  'update_national_id_title',
  'update_national_id_message',
  'bailiff_termination_title',
  'bailiff_termination_message',
] as const;

export type NotificationMessage = (typeof NOTIFICATION_MESSAGES)[number];

export const CUSTOM_NOTIFICATION_TYPE = 'communications.in_app';

export interface NotificationDynamicFields {
  readonly invitedBy: string;
  readonly workspaceName: string;
  readonly actionBy: string;
  readonly date: string;
  readonly userAffected: string;
}

export interface NotificationAction {
  readonly id: string;
  readonly name: NotificationActionName;
  readonly url: string;
  readonly destiny: NotificationActionDestiny;
  readonly type: NotificationActionType;
  readonly isActive: boolean;
}

export interface AppNotification {
  readonly id: string;
  readonly title: string;
  readonly message: string;
  readonly dynamicFields: NotificationDynamicFields | null;
  readonly date: string;
  readonly status: NotificationStatus;
  readonly type: string;
  readonly actions: readonly NotificationAction[];
}

export function isNotificationStatus(value: unknown): value is NotificationStatus {
  return typeof value === 'string' && (NOTIFICATION_STATUSES as readonly string[]).includes(value);
}

export function isNotificationActionName(value: unknown): value is NotificationActionName {
  return (
    typeof value === 'string' && (NOTIFICATION_ACTION_NAMES as readonly string[]).includes(value)
  );
}

export function isNotificationActionType(value: unknown): value is NotificationActionType {
  return (
    typeof value === 'string' && (NOTIFICATION_ACTION_TYPES as readonly string[]).includes(value)
  );
}

export function isNotificationActionDestiny(value: unknown): value is NotificationActionDestiny {
  return (
    typeof value === 'string' &&
    (NOTIFICATION_ACTION_DESTINIES as readonly string[]).includes(value)
  );
}

export function isNotificationMessage(value: unknown): value is NotificationMessage {
  return typeof value === 'string' && (NOTIFICATION_MESSAGES as readonly string[]).includes(value);
}
